import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  const XLogo = ({ size = 24, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );

  const TelegramLogo = ({ size = 24, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 0 0-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
    </svg>
  );

  const DiscordLogo = ({ size = 24, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026c.462-.62.874-1.275 1.226-1.963.021-.04.001-.088-.041-.104a13.201 13.201 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z"/>
    </svg>
  );

  const TikTokLogo = ({ size = 24, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );

  const experiences = [
    { 
      role: 'Ambassador', 
      project: 'Alpha Dao', 
      timeline: 'March 2025 – Present',
      description: 'A Decentralized Autonomous community, focused on Educating Web3 Enthusiasts using a Decentralized ecosystem system, giving users equal rights to vote on community decisions.',
      logo: '/images/Alpha Dao.png',
      website: 'https://x.com/Alpha_Daos'
    },
    { 
      role: 'Community Manager', 
      project: 'Alpha Vault', 
      timeline: 'October 2025 – Present',
      description: 'Alpha Vault is a Web3 community built for beginners and experienced users to learn, grow, and access valuable opportunities together. It provides education, insights, alphas, job opportunities, and events while rewarding active participation through its Alpha Points system.',
      logo: '/images/Alpha Vault.jpeg',
      website: 'https://t.me/+avuzxDDJ5a4xOGE8'
    },
    { 
      role: 'Builder', 
      project: 'DC Awards', 
      timeline: 'April 2025 – Present',
      description: 'DC_Awards is a community-driven awards platform that recognizes and rewards genuine contributors in the Web3, crypto, and forex ecosystem. It operates a transparent system where nominations and voting are fully controlled by the community to ensure fairness, equality, and merit-based recognition.',
      logo: '/images/DC Awards.jpeg',
      website: 'https://x.com/DC_awards_'
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/80 backdrop-blur-lg border-b border-blue-500/20' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              MR DY0R OF WEB3
            </div>
            
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('about')} className="hover:text-blue-400 transition">About</button>
              <button onClick={() => scrollToSection('experience')} className="hover:text-blue-400 transition">Experience</button>
              <button onClick={() => scrollToSection('skills')} className="hover:text-blue-400 transition">Skills</button>
              <button onClick={() => scrollToSection('contact')} className="hover:text-blue-400 transition">Contact</button>
            </div>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden">
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>

          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left hover:text-blue-400 transition">About</button>
              <button onClick={() => scrollToSection('experience')} className="block w-full text-left hover:text-blue-400 transition">Experience</button>
              <button onClick={() => scrollToSection('skills')} className="block w-full text-left hover:text-blue-400 transition">Skills</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left hover:text-blue-400 transition">Contact</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-cyan-900/20"></div>
        <div className="absolute inset-0 animate-grid" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          {/* Profile Picture */}
          <div className="flex justify-center mb-8">
            <div className="relative group">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 blur-2xl opacity-70 group-hover:opacity-90 transition-opacity duration-300"></div>
              <img 
                src="/images/profile.jpg"
                alt="MR_DY0R_OF_WEB3 Profile" 
                className="relative w-40 h-40 md:w-48 md:h-48 rounded-full object-cover ring-4 ring-blue-500/50 transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 hidden items-center justify-center text-6xl font-bold ring-4 ring-blue-500/50 transition-transform duration-300 group-hover:scale-105">
                MD
              </div>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent animate-pulse">
            MR_DY0R_OF_WEB3
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-4">
            Web3 Educator • Content Writer • Community Manager
          </p>
          <p className="text-lg md:text-xl text-blue-400 mb-12 italic">
            "The future of Web3 projects is education."
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://t.me/MR_DY0R_OF_WEB3" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 px-8 py-4 rounded-lg font-semibold transition transform hover:scale-105 shadow-lg shadow-blue-500/50">
              Hire Me
            </a>
            <button onClick={() => scrollToSection('experience')} className="border-2 border-blue-500 hover:bg-blue-500/10 px-8 py-4 rounded-lg font-semibold transition transform hover:scale-105">
              View Proof of Work
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-gradient-to-b from-black to-blue-950/20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            About Me
          </h2>
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-8 group">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 blur-xl opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
              <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-4xl md:text-5xl font-bold ring-4 ring-blue-500/50 transition-transform duration-300 group-hover:scale-105 overflow-hidden">
                <span className="relative z-10">MD</span>
              </div>
            </div>

            <div className="max-w-3xl">
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                I believe education is the foundation of every successful Web3 community. I bridge the gap between your product and your users by educating, simplifying, and guiding them on how to utilize your platform effectively. As a community manager, my role is to build strong, loyal, and highly engaged communities across the platforms where my presence adds the most value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Proof of Work
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {experiences.map((exp, idx) => (
              <div 
                key={idx} 
                className="bg-black/40 backdrop-blur-md border border-blue-500/30 rounded-lg overflow-hidden hover:border-blue-400 transition transform hover:scale-105 hover:shadow-xl hover:shadow-blue-500/20 cursor-pointer"
                onClick={() => toggleCard(idx)}
              >
                <div className="p-6">
                  {/* Project Logo and Name */}
                  <div className="flex items-center gap-4 mb-4">
                    <img 
                      src={exp.logo} 
                      alt={exp.project}
                      className="w-16 h-16 rounded-full border-2 border-blue-500/50 object-cover"
                    />
                    <div className="flex-1">
                      <a 
                        href={exp.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xl font-bold hover:text-blue-400 transition inline-block"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {exp.project}
                      </a>
                    </div>
                  </div>

                  <div className="flex justify-between items-start mb-2">
                    <div className="text-blue-400 font-semibold">{exp.role}</div>
                    <div className={`text-blue-400 transition-transform duration-300 ${expandedCard === idx ? 'rotate-180' : ''}`}>
                      ▼
                    </div>
                  </div>
                  <div className="text-gray-400 text-sm mb-4">{exp.timeline}</div>
                  
                  <div className={`overflow-hidden transition-all duration-500 ${expandedCard === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="pt-4 border-t border-blue-500/30">
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {exp.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-6 bg-gradient-to-b from-black to-blue-950/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Skills & Expertise
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              { skill: 'Graphics Design', level: 'Intermediate' },
              { skill: 'Video Editing', level: 'Intermediate' },
              { skill: 'Communication & Conflict Resolution', level: 'Expert' },
              { skill: 'Partnerships & Inter-Community Relations', level: 'Expert' }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-blue-950/40 to-cyan-950/40 backdrop-blur-sm border border-blue-500/30 rounded-lg p-6 hover:border-blue-400 transition">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-xl font-semibold">{item.skill}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${item.level === 'Expert' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500' : 'bg-blue-500/20 text-blue-400 border border-blue-500'}`}>
                    {item.level}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Book a Session Button */}
          <div className="flex justify-center">
            <a 
              href="https://twitter.com/MR_DY0R_OF_WEB3" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 px-10 py-4 rounded-lg font-semibold text-lg transition transform hover:scale-105 shadow-lg shadow-blue-500/50 flex items-center gap-3"
            >
              <XLogo size={24} />
              Book a Session
            </a>
          </div>
        </div>
      </section>

      {/* Community Platforms */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Active Platforms
          </h2>
          <div className="flex flex-wrap justify-center gap-8">
            {[
              { name: 'X (Twitter)', icon: <XLogo size={32} />, link: 'https://twitter.com/MR_DY0R_OF_WEB3' },
              { name: 'TikTok', icon: <TikTokLogo size={32} />, link: 'https://tiktok.com/@MR_DYOR' },
              { name: 'Telegram', icon: <TelegramLogo size={32} />, link: 'https://t.me/MR_DY0R_OF_WEB3' },
              { name: 'Discord', icon: <DiscordLogo size={32} />, link: 'https://discord.gg/hEZnsQ6c' }
            ].map((platform, idx) => (
              <a 
                key={idx} 
                href={platform.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-blue-950/40 to-cyan-950/40 backdrop-blur-sm border border-blue-500/30 rounded-lg p-6 w-32 h-32 flex flex-col items-center justify-center hover:border-blue-400 transition transform hover:scale-110 hover:shadow-xl hover:shadow-blue-500/20 cursor-pointer"
              >
                <div className="mb-2">{platform.icon}</div>
                <div className="text-sm font-semibold">{platform.name}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsorship Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-blue-950/20">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Sponsored By
          </h3>
          <div className="flex flex-row justify-center items-center gap-12">
            {[
              {
                name: 'FinanceHub',
                logo: '/images/FinanceHub.png',
                website: 'https://financehubmarket.pages.dev/'
              },
              {
                name: 'Alpha Daos',
                logo: '/images/Alpha Dao.png',
                website: 'https://x.com/Alpha_Daos'
              },
              {
                name: 'DC Awards',
                logo: '/images/DCA.png',
                website: 'https://x.com/DC_awards_'
              }
            ].map((sponsor, idx) => (
              <a
                key={idx}
                href={sponsor.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center group"
              >
                <div className="w-24 h-24 mb-3 rounded-full overflow-hidden transition transform hover:scale-110">
                  <img 
                    src={sponsor.logo} 
                    alt={`${sponsor.name} logo`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="text-xl font-bold text-center text-gray-300 group-hover:text-blue-400 transition">
                  {sponsor.name}
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 px-6 bg-gradient-to-b from-blue-950/20 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Let's Build Your Community
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            With clarity, trust, and education.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://t.me/MR_DY0R_OF_WEB3" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 px-8 py-4 rounded-lg font-semibold transition transform hover:scale-105 shadow-lg shadow-blue-500/50">
              Work With Me
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-blue-500/20">
        <div className="max-w-6xl mx-auto text-center text-gray-400">
          <p>&copy; 2025 MR_DY0R_OF_WEB3. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        @keyframes gridMove {
          0% { transform: translateY(0); }
          100% { transform: translateY(50px); }
        }
        .animate-grid {
          animation: gridMove 20s linear infinite;
        }
      `}</style>
    </div>
  );
}

export default App;